import * as React from "react";
import { connect } from "react-redux";
import { isRequestActive } from "redux-request-loading";
import { Grid, Loader, Form, Divider, Dropdown } from "semantic-ui-react";
import * as moment from "moment";
import { debounce } from "lodash";

import { Input } from "@neworbit/simpleui-input";

import { TaskModel, TasksAndCountModel } from "@teamprevent/common/task/taskActions/models";
import { debounceDelayMediumHigh } from "@teamprevent/common/filtering/constants";

import { TaskCategoryDropdown } from "../../tasks/components/TaskCategoryDropdown";
import { loadAdminUsers as loadAdminUsersAction } from "../../crud/adminUser";
import { CustomerInRedux, loadCustomers as loadCustomersAction } from "../../crud/customer";
import { Team } from "../../teams/model";
import { getUserSortableName, getUserDisplayName, AdminUserInRedux } from "../../crud/adminUser/model";
import { loadActionableTasks as loadActionableTasksAction } from "../actions";
import { loadTeams as loadTeamsAction } from "../../teams";
import { LOAD_ACTIONABLE_TASKS } from "../actionTypes";
import { ApplicationState } from "../../applicationState";
import { ALL_ADMIN_USERS, ALL_ADMIN_USERS_OPTION, ALL_ADMIN_USERS_VALUE } from "../models";
import { ActionableTasksApi } from "../actionableTasksApi";

interface OwnProps {
    onActionableTasksChanged(tasks: TaskModel[], tasksCount: number): void;
    userId: string;
    dueDate: moment.Moment;
    initialTaskTypes?: string[];
    preselectAllAdminUsers: boolean;
}

interface StateProps {
    actionableTasks: TasksAndCountModel;
    adminUsers: AdminUserInRedux[];
    teams: Team[];
    customers: CustomerInRedux[];
    loading: boolean;
}

interface DispatchProps {
    loadAdminUsers: () => void;
    loadTeams: () => void;
    loadCustomers: () => void;
    loadActionableTasks: (
        userIds: string[],
        teamIds: string[],
        customerIds: string[],
        taskCategories: string[],
        taskTypes: string[],
        dueDate: moment.Moment,
        includeWithoutDueDates: boolean,
        includeAssignedTasks: boolean) => void;
}

const ActionableTaskFilterUnconnected: React.FunctionComponent<StateProps & DispatchProps & OwnProps> = (props: StateProps & DispatchProps & OwnProps) => {
    const [userIds, setUserIds] = React.useState<string[]>([]);
    const [teamIds, setTeamIds] = React.useState<string[]>([]);
    const [customerIds, setCustomerIds] = React.useState<string[]>([]);
    const [taskCategories, setTaskCategories] = React.useState<string[]>([]);
    const [selectedTaskTypes, setSelectedTaskTypes] = React.useState<string[]>(props.initialTaskTypes || []);
    const [dueDate, setDueDate] = React.useState<moment.Moment>(props.dueDate);
    const [includeWithoutDueDates, setIncludeWithoutDueDates] = React.useState<boolean>(true);
    const [includeAssignedTasks, setIncludeAssignedTasks] = React.useState<boolean>(false);
    const [isLoading, setIsLoading] = React.useState<boolean>(true);

    const {
        loadTeams,
        loadCustomers,
        loadAdminUsers,
        adminUsers,
        teams,
        customers,
        onActionableTasksChanged,
        actionableTasks,
        loadActionableTasks,
    } = props;
    const [taskTypes, setTaskTypes] = React.useState<{ type: string; description: string }[]>([]);

    React.useEffect(() => loadTeams(), [loadTeams]);
    React.useEffect(() => loadCustomers(), [loadCustomers]);
    React.useEffect(() => {
        if (!adminUsers.length) {
            loadAdminUsers();
        }
    }, [adminUsers.length, loadAdminUsers]);

    React.useEffect(() => {
        const newUserIds = getInitialStateForUserIds(props.userId, props.preselectAllAdminUsers, props.adminUsers);
        setUserIds(newUserIds);
        setIsLoading(false);
    }, [props.userId, props.preselectAllAdminUsers, props.adminUsers]);

    React.useEffect(() => {
        let filteredActionableTasks = actionableTasks.tasks || [];
        if (!includeAssignedTasks) {
            filteredActionableTasks = filteredActionableTasks
                .filter(t => !t.assignedUsers || !t.assignedUsers.length || t.assignedUsers.some(u => userIds.indexOf(u.id) > -1));
        }
        filteredActionableTasks = filteredActionableTasks.sort((a, b) => (a.dueDate || moment()).diff(b.dueDate || moment()));
        onActionableTasksChanged(filteredActionableTasks, actionableTasks.totalCount);
    }, [actionableTasks, onActionableTasksChanged, includeAssignedTasks, userIds]);

    const updateFiltersCallback = React.useMemo(() =>
        debounce((
            adminUserIdsFilter,
            teamIdFilter,
            customerIdsFilter,
            taskCategoriesFilter,
            selectedTaskTypesFilter,
            dueDateFilter,
            includeWithoutDueDatesFilter,
            includeAssignedTasksFilter) => {
            loadActionableTasks(
                adminUserIdsFilter,
                teamIdFilter,
                customerIdsFilter,
                taskCategoriesFilter,
                selectedTaskTypesFilter,
                dueDateFilter,
                includeWithoutDueDatesFilter,
                includeAssignedTasksFilter);
        }, debounceDelayMediumHigh),
    [loadActionableTasks]);

    React.useEffect(() => {
        if (!isLoading) {
            const adminUserIds = userIds?.includes(ALL_ADMIN_USERS_VALUE.id) ? [] : userIds;
            updateFiltersCallback(adminUserIds, teamIds, customerIds, taskCategories, selectedTaskTypes, dueDate, includeWithoutDueDates, includeAssignedTasks);
        }
    }, [
        updateFiltersCallback, isLoading, userIds, teamIds, customerIds, taskCategories,
        selectedTaskTypes, dueDate, includeWithoutDueDates, includeAssignedTasks
    ]);

    React.useEffect(() => {
        const abortController = new AbortController();
        ActionableTasksApi.getTaskTypes(taskCategories, abortController).then(setTaskTypes);
        return () => abortController.abort();
    }, [taskCategories]);

    const getInitialStateForUserIds = (
        currentUserId: string,
        preselectAllAdminUsers: boolean,
        users: { id: string }[]
    ): string[] => {
        if (preselectAllAdminUsers) {
            return users.map(u => u.id);
        } else if (!preselectAllAdminUsers && currentUserId) {
            return [currentUserId];
        }
        return [];
    };

    const adminUserOptions = React.useMemo(() => [
        ALL_ADMIN_USERS_OPTION,
        ...adminUsers.map(adminUser =>
            ({ key: adminUser.id, value: adminUser.id, text: getUserDisplayName(adminUser) }))
    ], [adminUsers]);

    const teamsOptions = React.useMemo(() => teams.map(team => ({ key: team.id, value: team.id, text: team.name })), [teams]);

    const customerOptions = React.useMemo(() => {
        return customers.filter(c => c.active).map(customer => ({ key: customer.id, value: customer.id, text: customer.name }));
    }, [customers]);

    const taskTypeOptions = React.useMemo(() => taskTypes.map(taskType => ({ value: taskType.type, text: taskType.description })), [taskTypes]);

    const onUsersChange = React.useCallback((event: React.SyntheticEvent<HTMLElement>, { value }: any) => {
        const adminUserIds = value as string[];
        if (adminUserIds?.includes(ALL_ADMIN_USERS_VALUE.id)) {
            setUserIds([ALL_ADMIN_USERS]);
        } else {
            setUserIds(adminUserIds);
        }
    }, []);

    return (
        <>
            <h1>Actionable Tasks</h1>
            <Form>
                <Grid>
                    <Grid.Row columns="3">
                        <Grid.Column>
                            <Form.Field>
                                <label>Users</label>
                                <Dropdown
                                    value={userIds}
                                    onChange={onUsersChange}
                                    options={adminUserOptions}
                                    multiple
                                    search
                                    fluid
                                    selection
                                    labeled
                                />
                            </Form.Field>
                        </Grid.Column>
                        <Grid.Column>
                            <Input.DropdownMulti
                                dynamicOptions
                                value={teamIds}
                                label="Teams"
                                onChange={setTeamIds}
                                options={teamsOptions}
                                search
                            />
                        </Grid.Column>
                        <Grid.Column>
                            <Input.DropdownMulti
                                dynamicOptions
                                value={customerIds}
                                label="Customers"
                                onChange={setCustomerIds}
                                options={customerOptions}
                                search
                            />
                        </Grid.Column>
                        <Grid.Column>
                            <TaskCategoryDropdown
                                selectedTaskCategories={taskCategories}
                                onSelectionChanged={setTaskCategories}
                            />
                        </Grid.Column>
                        <Grid.Column>
                            <Input.DropdownMulti
                                dynamicOptions
                                value={selectedTaskTypes}
                                label="Task Types"
                                onChange={setSelectedTaskTypes}
                                options={taskTypeOptions}
                                search
                            />
                        </Grid.Column>
                        <Grid.Column>
                            <Input.Date
                                value={dueDate}
                                label="Due Date Until"
                                onChange={setDueDate}
                            />
                        </Grid.Column>
                        <Grid.Column>
                            <Divider hidden />
                            <Input.Checkbox
                                value={includeAssignedTasks}
                                label="Include assigned tasks"
                                onChange={setIncludeAssignedTasks}
                            />
                        </Grid.Column>
                        <Grid.Column>
                            <Divider hidden />
                            <Input.Checkbox
                                value={includeWithoutDueDates}
                                label="Include tasks without due dates"
                                onChange={setIncludeWithoutDueDates}
                            />
                        </Grid.Column>
                        <Grid.Column />
                    </Grid.Row>
                </Grid>
            </Form>
            <Loader active={props.loading} />
        </>
    );
};

function mapStateToProps(state: ApplicationState): StateProps {
    return {
        actionableTasks: state.actionableTasks,
        adminUsers: state.adminUsers.sort((a, b) => getUserSortableName(a).localeCompare(getUserSortableName(b))),
        teams: state.teams,
        customers: state.customers,
        loading: isRequestActive(state, LOAD_ACTIONABLE_TASKS)
    };
}

const mapDispatchToProps: DispatchProps = ({
    loadAdminUsers: () => loadAdminUsersAction(),
    loadTeams: () => loadTeamsAction(),
    loadCustomers: () => loadCustomersAction(),
    loadActionableTasks: (
        userIds: string[],
        teamIds: string[],
        customerIds: string[],
        taskCategories: string[],
        taskTypes: string[],
        dueDate: moment.Moment,
        includeWithoutDueDates: boolean,
        includeAssignedTasks: boolean) =>
        loadActionableTasksAction(userIds, teamIds, customerIds, taskCategories, taskTypes, dueDate, includeWithoutDueDates, includeAssignedTasks)
});

export const ActionableTaskFilter = connect(mapStateToProps, mapDispatchToProps)(ActionableTaskFilterUnconnected);
